/**
 * Remembered file handles — the "where the browser offers persistent handles"
 * half of ADR-032 D6, and what makes the menu's *Recent* a list of files this
 * browser can actually reopen rather than names it can only recite.
 *
 * A `FileSystemFileHandle` from `showOpenFilePicker` is structured-cloneable,
 * so IndexedDB can keep it across reloads (localStorage takes strings and
 * cannot). On a later open the handle is tried FIRST for each `upload:` a
 * Document names: `queryPermission` says whether the browser still grants it,
 * `requestPermission` may ask — but only from a click, so the door's own
 * relink (openDocument) never prompts and the menu's relink entry may.
 *
 * FAIL-SOFT, ONCE, IN THE CONSOLE AND ON THE MENU. IndexedDB can be absent,
 * blocked, or refuse a write (a handle from a fallback path is not cloneable).
 * The first failure turns `available()` off for the session and says so in
 * the console; every method thereafter resolves to "nothing remembered". The
 * menu renders `available()` as the reason its Recent list is empty, so the
 * absence reaches the eye — never a silent empty list.
 *
 * Depends on: DOM (IndexedDB) only. Depended on by: main; file-menu (types).
 */

export type RememberedKind = 'character' | 'animation' | 'document';

export interface RememberedFile {
  /** `upload:<filename>` for a character or animation; `document:<filename>` for a Document. */
  readonly uri: string;
  readonly name: string;
  readonly kind: RememberedKind;
  /** ISO timestamp of the open — Recent sorts newest first. */
  readonly at: string;
}

/**
 * The slice of FileSystemFileHandle this app touches. The permission methods
 * are Chromium's and absent from lib.dom, hence optional here.
 */
export interface FileHandleLike {
  getFile(): Promise<File>;
  queryPermission?(descriptor: { readonly mode: 'read' }): Promise<'granted' | 'denied' | 'prompt'>;
  requestPermission?(descriptor: {
    readonly mode: 'read';
  }): Promise<'granted' | 'denied' | 'prompt'>;
}

export interface FileHandles {
  /** False once IndexedDB has refused — the reason Recent has nothing to show. */
  available(): boolean;
  remember(entry: RememberedFile, handle: FileHandleLike): Promise<void>;
  recall(uri: string): Promise<FileHandleLike | null>;
  /** Newest first, without the handles. */
  recent(limit: number): Promise<readonly RememberedFile[]>;
}

const STORE = 'files';

export function createFileHandles(dbName = 'animator.files.v1'): FileHandles {
  let ok = typeof indexedDB !== 'undefined';
  let dbPromise: Promise<IDBDatabase | null> | null = null;

  const lost = (op: string, err: unknown): void => {
    if (!ok) return;
    ok = false;
    console.warn(
      `[animator-v3] file handles unavailable — IndexedDB ${op} failed; Recent and relink-by-handle are off for this session`,
      err,
    );
  };

  const open = (): Promise<IDBDatabase | null> => {
    if (!ok) return Promise.resolve(null);
    if (dbPromise !== null) return dbPromise;
    dbPromise = new Promise((resolve) => {
      try {
        const req = indexedDB.open(dbName, 1);
        req.onupgradeneeded = () => {
          if (!req.result.objectStoreNames.contains(STORE))
            req.result.createObjectStore(STORE, { keyPath: 'uri' });
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => {
          lost('open', req.error);
          resolve(null);
        };
        req.onblocked = () => {
          lost('open (blocked by another tab)', null);
          resolve(null);
        };
      } catch (err) {
        lost('open', err);
        resolve(null);
      }
    });
    return dbPromise;
  };

  /** One request in one transaction; any failure is the session's one warning and a null. */
  const request = async <T>(
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T | null> => {
    const db = await open();
    if (db === null) return null;
    return new Promise((resolve) => {
      try {
        const req = run(db.transaction(STORE, mode).objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => {
          lost(mode, req.error);
          resolve(null);
        };
      } catch (err) {
        lost(mode, err);
        resolve(null);
      }
    });
  };

  return {
    available: () => ok,
    async remember(entry, handle) {
      await request('readwrite', (store) => store.put({ ...entry, handle }));
    },
    async recall(uri) {
      const row = await request<{ handle?: FileHandleLike } | undefined>('readonly', (store) =>
        store.get(uri),
      );
      return row?.handle ?? null;
    },
    async recent(limit) {
      const rows =
        (await request<Array<RememberedFile & { handle?: unknown }>>('readonly', (store) =>
          store.getAll(),
        )) ?? [];
      return rows
        .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0))
        .slice(0, limit)
        .map(({ uri, name, kind, at }) => ({ uri, name, kind, at }));
    },
  };
}

/**
 * Does the browser grant this remembered handle right now? `mayPrompt` is
 * true only from a click — the permission dialog needs a gesture, and a door
 * that opened on its own must not throw one at a person. False on refusal or
 * on a handle that throws (revoked, another origin); the why is kept.
 */
export async function grantHandle(
  handle: FileHandleLike,
  opts: { readonly mayPrompt: boolean },
): Promise<boolean> {
  try {
    let state = handle.queryPermission ? await handle.queryPermission({ mode: 'read' }) : 'granted';
    if (state === 'prompt' && opts.mayPrompt && handle.requestPermission) {
      state = await handle.requestPermission({ mode: 'read' });
    }
    return state === 'granted';
  } catch (err) {
    console.warn('[animator-v3] a remembered file handle refused', err);
    return false;
  }
}

/**
 * The file behind a remembered handle, or null when the browser no longer
 * grants it or the file moved. Grant and read in ONE step — for a door that
 * already holds its load sequence (the Document door's own relink). The menu
 * does not use this: it grants first, reserves its order ticket, and reads
 * afterwards, so a slow read cannot be overtaken (Codex P1, #140).
 */
export async function fileFromHandle(
  handle: FileHandleLike,
  opts: { readonly mayPrompt: boolean },
): Promise<File | null> {
  if (!(await grantHandle(handle, opts))) return null;
  try {
    return await handle.getFile();
  } catch (err) {
    // NotFoundError (moved) — the same answer for the caller: try the next way in.
    console.warn('[animator-v3] a remembered file could not be read (moved?)', err);
    return null;
  }
}
